import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg, COMPANY_SIZES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Pencil, Loader2 } from "lucide-react";

export const CompanyProfileForm = ({ existing, onSaved }) => {
  const [form, setForm] = useState({
    company_name: existing?.company_name || "",
    company_size: existing?.company_size || "small",
    industry: existing?.industry || "",
    website: existing?.website || "",
    description: existing?.description || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/client/profile", form);
      toast.success("Company profile saved");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err, "Could not save profile"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Company name</Label>
          <Input data-testid="company-name-input" required value={form.company_name}
            onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="rounded-xl bg-card" />
        </div>
        <div className="space-y-1.5">
          <Label>Company size</Label>
          <Select value={form.company_size} onValueChange={(v) => setForm({ ...form, company_size: v })}>
            <SelectTrigger data-testid="company-size-select" className="rounded-xl bg-card"><SelectValue /></SelectTrigger>
            <SelectContent>
              {COMPANY_SIZES.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Industry</Label>
          <Input data-testid="company-industry-input" value={form.industry} placeholder="e.g. Swimwear e-commerce"
            onChange={(e) => setForm({ ...form, industry: e.target.value })} className="rounded-xl bg-card" />
        </div>
        <div className="space-y-1.5">
          <Label>Website</Label>
          <Input data-testid="company-website-input" value={form.website} placeholder="https://…"
            onChange={(e) => setForm({ ...form, website: e.target.value })} className="rounded-xl bg-card" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>What does your company do?</Label>
        <Textarea data-testid="company-description-input" rows={3} value={form.description}
          placeholder="Used by our AI to screen your jobs and suggest fair rates."
          onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl bg-card" />
      </div>
      <Button data-testid="company-profile-save-button" type="submit" disabled={busy} className="rounded-xl">
        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save company profile
      </Button>
    </form>
  );
};

export const CompanyProfileCard = ({ stats, editing, onEdit, onSaved }) => (
  <Card className="rounded-2xl">
    <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
      <CardTitle className="flex items-center gap-2 text-base">
        <Building2 className="h-4 w-4" /> Company profile
      </CardTitle>
      {stats?.client_profile && !editing && (
        <Button data-testid="company-profile-edit-button" variant="ghost" size="sm" className="rounded-xl" onClick={onEdit}>
          <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
        </Button>
      )}
    </CardHeader>
    <CardContent>
      {!stats?.client_profile || editing ? (
        <>
          {!stats?.client_profile && (
            <p className="mb-4 rounded-xl border-2 border-[hsl(43,74%,70%)] bg-[hsl(43,74%,95%)] p-3 text-sm">
              Complete your company information to describe jobs and book models — required before booking.
            </p>
          )}
          <CompanyProfileForm existing={stats?.client_profile} onSaved={onSaved} />
        </>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Company</p>
            <p data-testid="company-profile-name" className="font-semibold">{stats.client_profile.company_name}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Size</p>
            <p className="font-semibold capitalize">{stats.client_profile.company_size}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Industry</p>
            <p className="font-semibold">{stats.client_profile.industry || "—"}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Website</p>
            <p className="truncate font-semibold">{stats.client_profile.website || "—"}</p>
          </div>
          {stats.client_profile.description && (
            <p className="text-sm text-muted-foreground sm:col-span-2">{stats.client_profile.description}</p>
          )}
        </div>
      )}
    </CardContent>
  </Card>
);
